import { useRef, useState } from "react";
import Columns from "../../../library/components/layout/Columns";
import Rows from "../../../library/components/layout/Rows";
import { SarkiModel } from "../../../models/businessModels/SarkiModel";
import { Button, Typography, FormControl, Switch, Box } from "@mui/material";
import { appIcons } from "../../../app/appIcons";
import CsmTextField from "../../../library/components/mui/common/CsmTextField";
import JsonHelper from "../../../library/helpers/JsonHelper";
import { HesapBilgiVeKullaniciModel } from "../../../models/businessModels/HesapBilgiVeKullaniciModel";
import { HesapSarkiSesKayitModel } from "../../../models/businessModels/HesapSarkiSesKayitModel";
import { EnmKayitDurum } from "../../../models/enums/EnmKayitDurum";
import { CsmText } from "../../../library/components/mui/CsmText";
import GenelDurumHelper from "../../../helpers/GenelDurumHelper";
import PdfDisplayer from "../../../components/media/PdfDisplayer";
import RegularAudioPlayer, { IRegularAudioPlayerHandle } from "../../../components/media/RegularAudioPlayer";
import appColors from "../../../app/appColors";
import Cell from "../../../library/components/layout/Cell";
import { HesapKayitYorumRequestModel } from "../../../models/requestModels/HesapKayitYorumRequestModel";
import KorocanThumbnailForHesap from "../../../components/business/KorocanThumbnailForHesap";
import StringHelper from "../../../library/helpers/StringHelper";
import { BusinessHelper } from "../../../helpers/BusinessHelper";
import { EnmYetki } from "../../../models/enums/EnmYetki";
import PdfDisplayerWithCanvas, { IPdfDisplayerWithCanvasHandle } from "../../../components/media/PdfDisplayerWithCanvas";
import { GuidHelper } from "../../../library/helpers/GuidHelper";
import { IcerikEklemeRequestModel } from "../../../models/requestModels/IcerikEklemeRequestModel";
import { HesapKayitYorumlaRequestModel } from "../../../models/requestModels/HesapKayitYorumlaRequestModel";
import { HesapSarkiSesKayitNotlarModel } from "../../../models/businessModels/HesapSarkiSesKayitNotlarModel";

interface IKayitWithPdfPopupEditorProps {
    sarki: SarkiModel,
    yorumKaydedebilir: boolean,
    hesapBilgi: HesapBilgiVeKullaniciModel,
    hesapSarkiSesKayit: HesapSarkiSesKayitModel,
    closer: (model: HesapSarkiSesKayitModel | null) => void,
    genelDurumHelper: GenelDurumHelper,
}

function KayitWithPdfPopupEditor(props: IKayitWithPdfPopupEditorProps) {
    const [yorumlar, setYorumlar] = useState<string>(StringHelper.nullToEmpty(props.hesapSarkiSesKayit.yorumlar));
    const [kayitUygun, setKayitUygun] = useState<boolean>(props.hesapSarkiSesKayit.kayitDurum === EnmKayitDurum.Onaylandı);

    const gorselYorumlamaKullan = props.genelDurumHelper.SeciliKoroModel.koroAyarlari.gorselYorumlamaKullan;
    const kayitSonDurum = !props.yorumKaydedebilir ? props.hesapSarkiSesKayit.kayitDurum : (kayitUygun ? EnmKayitDurum.Onaylandı : EnmKayitDurum.Hatalı)

    const adSoyad = `${props.hesapBilgi.kullaniciModel.ad} ${props.hesapBilgi.kullaniciModel.soyad}`;

    const guncellemeYetki = props.genelDurumHelper.HasYetki(EnmYetki["Kayıt Onaylama"]);

    const { pdfVariMi, pdfIcerikId } = BusinessHelper.Icerik.SarkiLinktenPdfLinkGetir(props.sarki?.sarkiLinkModelList);

    const eskiResimIcerikId = props.hesapSarkiSesKayit.resimIcerikId;
    const orijinalPdfIcerikId = props.hesapSarkiSesKayit.orijinalPdfIcerikId;
    const seciliPdfIcerikId = StringHelper.isNotEmpty(orijinalPdfIcerikId) ? orijinalPdfIcerikId! : pdfIcerikId;

    const pdfCanvasRef = useRef<IPdfDisplayerWithCanvasHandle>(null);
    const audioPlayerRefLg = useRef<IRegularAudioPlayerHandle>(null);
    const audioPlayerRefSm = useRef<IRegularAudioPlayerHandle>(null);

    const getAudioSure = () =>
        audioPlayerRefLg.current?.getCurrentTimeSeconds()
        ?? audioPlayerRefSm.current?.getCurrentTimeSeconds()
        ?? 0;

    const playAudioAtSure = (sure: number) => {
        const player = audioPlayerRefLg.current ?? audioPlayerRefSm.current;
        player?.seekAndPlayAtSeconds(sure);
    };

    const canvasInitialNotlar = props.yorumKaydedebilir
        ? props.hesapSarkiSesKayit.notlar
        : (props.hesapSarkiSesKayit.notlar ?? []);

    const notlarForRequest = (): HesapSarkiSesKayitNotlarModel[] => {
        const kaynak = gorselYorumlamaKullan
            ? (pdfCanvasRef.current?.getNotlar() ?? [])
            : (props.hesapSarkiSesKayit.notlar ?? []);
        return kaynak.map(n => {
            const not = JsonHelper.deepCopy(n);
            not.hesapSarkiSesKayitModelId = props.hesapSarkiSesKayit.id;
            return not;
        });
    };

    const yorumKaydet = async () => {
        const icerikList: IcerikEklemeRequestModel[] = [];
        let yeniResimIcerikId = eskiResimIcerikId;

        const canvasChanged = gorselYorumlamaKullan && pdfCanvasRef.current?.hasChanges() === true;
        if (canvasChanged) {
            const pngBase64 = await pdfCanvasRef.current?.exportLongPngBase64();
            if (StringHelper.isNotEmpty(pngBase64)) {
                yeniResimIcerikId = GuidHelper.NewGuid();
                icerikList.push(new IcerikEklemeRequestModel(yeniResimIcerikId, ".png", pngBase64!));
            }
        }

        const model = new HesapKayitYorumlaRequestModel(
            new HesapKayitYorumRequestModel(props.hesapSarkiSesKayit.id, kayitUygun, yorumlar, yeniResimIcerikId, seciliPdfIcerikId, notlarForRequest()),
            icerikList
        );

        const webResponse = await props.genelDurumHelper.requestHelper.YonetimHesapMuzikal.HesapKayitYorumla(model);
        if (!webResponse.successfull)
            return;

        const hesapSarkiSesKayit = JsonHelper.deepCopy(props.hesapSarkiSesKayit);
        hesapSarkiSesKayit.kayitDurum = kayitUygun ? EnmKayitDurum.Onaylandı : EnmKayitDurum.Hatalı;
        hesapSarkiSesKayit.yorumlar = yorumlar;
        hesapSarkiSesKayit.resimIcerikId = yeniResimIcerikId;
        hesapSarkiSesKayit.orijinalPdfIcerikId = seciliPdfIcerikId;
        hesapSarkiSesKayit.notlar = notlarForRequest();
        props.closer(hesapSarkiSesKayit);
    }

    const yorumBox = props.yorumKaydedebilir ?
        <CsmTextField
            readonly={!props.yorumKaydedebilir}
            multiline
            disabled={!guncellemeYetki}
            error={guncellemeYetki && !yorumlar}
            rows={4}
            label="Yorumlar"
            value={yorumlar}
            showStats
            eventOnKeyPress
            onChange={(e) => setYorumlar(() => e)} />
        :
        <div style={{ overflowY: "auto" }}>
            <CsmText>YORUMLAR: {yorumlar}</CsmText>
        </div>;

    const bilgBoxRenk = kayitSonDurum === EnmKayitDurum.Onaylandı ? appColors.LIGHTGREEN : kayitSonDurum === EnmKayitDurum["Yorum Bekleniyor"] ? appColors.LIGHTYELLOW : appColors.LIGHTRED;
    const textRenk = kayitSonDurum === EnmKayitDurum.Onaylandı ? appColors.GREEN_MAIN : kayitSonDurum === EnmKayitDurum["Yorum Bekleniyor"] ? appColors.YELLOW : appColors.RED_MAIN;
    const textIcerik = kayitSonDurum === EnmKayitDurum.Onaylandı ? "Kayıt Başarılı" : kayitSonDurum === EnmKayitDurum["Yorum Bekleniyor"] ? "Yorum Bekliyor" : "Kayıt Hatalı";

    const bilgiBox = <Rows width={250} mb={5} p={5} style={{ background: bilgBoxRenk, borderRadius: 15 }}>
        <Cell>
            <CsmText bold style={{ textAlign: "center" }}>{props.sarki.isim}</CsmText>
        </Cell>
        <Columns>
            <KorocanThumbnailForHesap style={{ marginLeft: "5px" }} hesap={props.hesapBilgi} size="normal" genelDurumHelper={props.genelDurumHelper} />
            <Rows>
                <CsmText note style={{ marginLeft: "5px" }}>{props.genelDurumHelper.SesGruplariniGetir([props.hesapSarkiSesKayit.sesGrupId]).singleOrDefault()?.isim ?? "Belirsiz"}</CsmText>
                <CsmText note style={{ marginLeft: "5px" }}>{adSoyad}</CsmText>
                {props.yorumKaydedebilir ?
                    <FormControl style={{ marginRight: 5, display: "flex", flexDirection: "row", placeSelf: "center" }}>
                        <Switch checked={kayitUygun} onChange={(e) => setKayitUygun(e.target.checked)} disabled={!guncellemeYetki} />
                        <Typography sx={{ marginTop: "8px", color: textRenk }}>{kayitUygun ? "Başarılı" : "Hatalı"}</Typography>
                    </FormControl>
                    :
                    <Typography sx={{ marginTop: "8px", marginLeft: "5px", placeSelf: "center" }}>Durum: {textIcerik}</Typography>
                }
            </Rows>
        </Columns>
    </Rows>;

    return <Box height="100%" sx={{ minWidth: { xs: 300, md: 850, lg: 1024, xl: 1445 } }}>
        <Rows height="100%">
            <Columns height={140}>
                {bilgiBox}
                <FormControl style={{ flex: 1, marginLeft: 5, marginTop: 5, marginBottom: 5 }}>
                    {yorumBox}
                </FormControl>
                <Box sx={{ display: { xs: 'none', md: 'none', lg: 'flex', xl: 'flex', }, flex: 1, padding: "5px" }}>
                    <RegularAudioPlayer ref={audioPlayerRefLg} genelDurumHelper={props.genelDurumHelper} icerikId={props.hesapSarkiSesKayit.sesIcerikId} title={`${adSoyad} - ${props.sarki.isim} Kaydı`} />
                </Box>
            </Columns>
            <Box sx={{ display: { xs: 'block', md: 'block', lg: 'none', xl: 'none', } }}>
                <Columns mb={10}>
                    <RegularAudioPlayer ref={audioPlayerRefSm} genelDurumHelper={props.genelDurumHelper} icerikId={props.hesapSarkiSesKayit.sesIcerikId} title={`${adSoyad} - ${props.sarki.isim} Kaydı`} />
                </Columns>
            </Box>
            <Cell flex style={{ display: "flex", justifyContent: "center", overflowY: "hidden" }}>
                {!pdfVariMi ? <CsmText>PDF Bulunmamaktadır</CsmText> :
                    <Rows width="100%" flex height="100%" style={{ minHeight: 0 }}>
                        {gorselYorumlamaKullan ?
                            <PdfDisplayerWithCanvas
                                ref={pdfCanvasRef}
                                genelDurumHelper={props.genelDurumHelper}
                                icerikId={seciliPdfIcerikId}
                                initialImageIcerikId={eskiResimIcerikId}
                                initialNotlar={canvasInitialNotlar}
                                readonly={!props.yorumKaydedebilir}
                                getAudioSure={getAudioSure}
                                playAudioAtSure={playAudioAtSure}
                            />
                            :
                            <PdfDisplayer genelDurumHelper={props.genelDurumHelper} icerikId={seciliPdfIcerikId} />
                        }
                    </Rows>
                }
            </Cell>
            {props.yorumKaydedebilir ?
                <Columns mt={10}>
                    <Button color="error" variant="contained" onClick={() => props.closer(null)} style={{ flex: 1, margin: 10, marginLeft: 0 }}>
                        {appIcons.Popup.iptal}
                        {props.genelDurumHelper.translate("İptal")}</Button>
                    <Button color="success" variant="contained" disabled={!guncellemeYetki || !yorumlar} onClick={() => yorumKaydet()} style={{ flex: 1, margin: 10, marginRight: 0 }}>
                        {appIcons.Popup.tamam}
                        {props.genelDurumHelper.translate("Kaydet")}</Button>
                </Columns>
                :
                <Columns mt={10}>
                    <Button color="info" variant="contained" onClick={() => props.closer(null)} style={{ flex: 1, margin: 10, marginLeft: 0 }}>
                        Tamam</Button>
                </Columns>
            }
        </Rows>
    </Box>
}

export default KayitWithPdfPopupEditor;